import {appAxios} from '@/axios'
import {router} from '@/router/router'
import {wsBroadcast, wsProgramBroadcast} from '@/store/wsBroadcast.js'
import {indexedStore} from "@/indexedDB/indexedStore.js"
import {yandexMetrika} from "@/external/yandexWebisor.js"
import {paymentStatuses, periodInDays, subscriptionStatuses} from '@/configs/subscriptionStatuses.js'
import store from '@/store/store.js'
import {appType, appTypes, loadTelegramSdk} from '@/appType/appType.js'
import {profileModerationStatus, virtualModerationStatus} from '@/configs/profileModerationStatus.js'
import {settings} from "@/configs/settings.js";

const SUBSCRIPTION_STATUS_PRIORITY = ['active', 'disabled', 'new']

function resolveCurrentSubscription(subscriptions) {
  if (!subscriptions || !subscriptions.length) {
    return null
  }

  const allStatuses = subscriptions.map(s => s.status)
  const currentStatus = SUBSCRIPTION_STATUS_PRIORITY.find(p => allStatuses.includes(p)) || null

  if (!currentStatus) {
    return null
  }

  if (
    currentStatus === subscriptionStatuses.new
    && subscriptions.every(sub => sub.status === subscriptionStatuses.new)
  ) {
    const subscriptionsWithPendingLastPayment = subscriptions
      .map(sub => ({
        subscription: sub,
        lastPayment: resolveLastPayment(sub.payments),
      }))
      .filter(({lastPayment}) => lastPayment?.status === paymentStatuses.pending)

    if (subscriptionsWithPendingLastPayment.length) {
      return subscriptionsWithPendingLastPayment.reduce((latest, current) =>
        new Date(current.lastPayment.created_at) > new Date(latest.lastPayment.created_at)
          ? current
          : latest
      ).subscription
    }
  }

  return subscriptions.find(sub => sub.status === currentStatus) ?? null
}

function resolveLastPayment(payments) {
  if (!payments?.length) {
    return null
  }

  return payments.reduce((latest, payment) =>
    new Date(payment.created_at) > new Date(latest.created_at) ? payment : latest
  )
}

function sortSubscriptions(subscriptions) {
  if (!subscriptions?.length) {
    return subscriptions
  }

  return [...subscriptions].sort((a, b) => {
    const aIndex = SUBSCRIPTION_STATUS_PRIORITY.indexOf(a.status)
    const bIndex = SUBSCRIPTION_STATUS_PRIORITY.indexOf(b.status)
    const aRank = aIndex === -1 ? SUBSCRIPTION_STATUS_PRIORITY.length : aIndex
    const bRank = bIndex === -1 ? SUBSCRIPTION_STATUS_PRIORITY.length : bIndex
    return aRank - bRank
  })
}

export const user = {
  namespaced: true,

  state: () => ({
    isRestoring: true,
    isAuthorized: false,
    token: null,
    profile: null,
    directories: null,
    roulette: null,
    baseDirectories: null,
    currentTrainingProgram: null,
    faqContinuation: false,
    /** Последнее WS payment_update: { payment, tg_chats } */
    paymentUpdate: null,
  }),

  getters: {
    moderationStatus(state, getters) {
      if(state.profile.moderation_status === profileModerationStatus.new && !getters.profileProgress.isEnoughFilled) {
        return virtualModerationStatus.incomplete
      }
      if(state.profile.moderation_status === profileModerationStatus.new && getters.profileProgress.isEnoughFilled) {
        return virtualModerationStatus.moderation
      }
      if(state.profile.moderation_status === profileModerationStatus.rejected) {
        return virtualModerationStatus.rejected
      }
      if(state.profile.moderation_status === profileModerationStatus.active) {
        return virtualModerationStatus.active
      }
      return virtualModerationStatus.incomplete
    },
    currentSubscription(state) {
      return resolveCurrentSubscription(state.profile?.subscriptions)
    },
    lastPayment(state, getters) {
      return resolveLastPayment(getters.currentSubscription?.payments)
    },
    subscriptionStatus(state, getters) {
      return getters.currentSubscription?.status ?? subscriptionStatuses.new
    },
    pendingSubscriptions(state) {
      const subscriptions = state.profile?.subscriptions

      if (!subscriptions || !subscriptions.length) {
        return []
      }

      return subscriptions.filter(sub => {
        if (sub.status === subscriptionStatuses.pending) {
          return true
        }

        const lastPayment = resolveLastPayment(sub.payments)

        return lastPayment?.status === paymentStatuses.pending
      })
    },
    isSubscriptionActive(state, getters) {
      return getters.subscriptionStatus === subscriptionStatuses.active
    },
    betterSubscription(state, getters) {
      const subscriptionPrices = state.directories?.subscription_price_list
      if (!subscriptionPrices?.length) {
        return null
      }

      const bestPeriodDays = Math.max(...subscriptionPrices.map((price) => periodInDays(price)))
      const better = subscriptionPrices.find((price) => periodInDays(price) === bestPeriodDays) ?? null
      if (!better || !bestPeriodDays) {
        return null
      }

      const currentPrice = getters.currentSubscription?.price
      if (currentPrice && periodInDays(currentPrice) >= bestPeriodDays) {
        return null
      }
      if (currentPrice?.id === better.id) {
        return null
      }

      return better
    },
    education(state) {
      return state.profile?.education?.at(0)
    },
    profileAccessTierOrder(state) {
      return state.profile?.profile_access_tier_order
    },
    profile(state) {
      return state.profile;
    },
    profileProgress(state) {
      const progress_fields_fullness = state.profile?.progress_fields_fullness
      const progress = {
        isFullFilled: false,
        isEnoughFilled: false,
        fullFilledCount: 0,
        enoughFilledCount: 0,
        fullFields: [
          {
            keys: ['products'],
            title: 'Продукты',
            route: {name: 'CheckListEditProducts'},
            isFilled: false,
          },
          {
            keys: ['userskill_set'],
            title: 'Ищу',
            route: {name: 'CheckListEditLookingFor'},
            isFilled: false,
          },
        ],
        enoughFields: [
          {
            keys: ['first_name', 'last_name', 'city', 'email'], // 'last_name'
            title: 'Личные данные',
            route: {name: 'CheckListEditPersonalData'},
            isFilled: false,
          },
          {
            keys: ['avatar'],
            title: 'Фото',
            route: {name: 'CheckListEditProfile'},
            isFilled: false,
          },
          {
            keys: ['profession_or_specialization_filled'],
            title: 'Роль',
            route: {name: 'CheckListEditProfessions'},
          },
          {
            keys: ['userniche_set'],
            title: 'Ниши',
            route: {name: 'CheckListEditNiches'},
            isFilled: false,
          },
          {
            keys: ['userachievement_set'],
            title: 'Кейсы и достижения',
            route: {name: 'CheckListEditAchievements'},
            isFilled: false,
          },
          {
            keys: ['usereducation_set'],
            title: 'Образование',
            route: {name: 'CheckListEditEducations'},
          },
          {
            keys: ['contact_info_filled'],
            title: 'Контакты',
            route: {name: 'CheckListEditContacts'},
          },
          {
            keys: ['about_me'],
            title: 'О себе',
            route: {name: 'CheckListEditDescription'},
            isFilled: false,
          },
        ],
      }
      if (!progress_fields_fullness) {
        return progress
      }
      progress.enoughFields.forEach((item) => {
        item.isFilled = item.keys.every(key => {
          return progress_fields_fullness[key]
        })
      })
      progress.fullFields.forEach((item) => {
        item.isFilled = item.keys.every(key => progress_fields_fullness[key])
      })
      progress.isEnoughFilled = progress.enoughFields.every(i => {
        return i.isFilled
      })
      progress.isFullFilled = progress.isEnoughFilled && progress.fullFields.every(i => {
        return i.isFilled
      })
      progress.enoughFields.forEach(i => {
        if (i.isFilled) {
          progress.enoughFilledCount++
        }
      })
      progress.fullFields.forEach(i => {
        if (i.isFilled) {
          progress.fullFilledCount++
        }
      })
      return progress
    },
    bodyBaseProfileProgress({ profile }) {
      const progressVolumes = {
        first_name: {
          value: profile.first_name ? 2.5 : 0,
          onwardsStep: 1
        },
        last_name: {
          value: profile.last_name ? 2.5 : 0,
          onwardsStep: 1
        },
        email: {
          value: profile.email ? 2.5 : 0,
          onwardsStep: 1
        },
        birth_date: {
          value: profile.birth_date ? 2.5 : 0,
          onwardsStep: 1
        },
        city: {
          value: profile.city ? 2.5 : 0,
          onwardsStep: 1
        },
        sex: {
          value: profile.sex ? 4.17 : 0,
          onwardsStep: 2
        },
        height: {
          value: profile.height ? 4.17 : 0,
          onwardsStep: 2
        },
        weight: {
          value: profile.weight ? 4.17 : 0,
          onwardsStep: 2
        },
        target: {
          value: profile.targets.length ? 12.5 : 0,
          onwardsStep: 3
        },
        looking_for: {
          value: profile.looking_for.length ? 12.5 : 0,
          onwardsStep: 4
        },
        personal_records: {
          value: profile.personal_records.length ? 12.5 : 0,
          onwardsStep: 5
        },
        trainings_frequency: {
          value: profile.trainings_frequency.length ? 12.5 : 0,
          onwardsStep: 6
        },
        progresses: {
          value: profile.progresses.length ? 12.5 : 0,
          onwardsStep: 7
        },
        health_services: {
          value: profile.health_services.length ? 12.5 : 0,
          onwardsStep: 8
        }
      }

      return {
        summary: Object.keys(progressVolumes).reduce((acc, key) => {
          acc += progressVolumes[key].value
          return acc > 100 ? 100 : acc
        }, 0),
        onwardsEmptyStep: progressVolumes[Object.keys(progressVolumes).find((key) => {
          return !progressVolumes[key].value
        })]?.onwardsStep
      }
    },
    directories(state) {
      return state.directories
    }
  },
  mutations: {
    setFaqContinuation(state, value) {
      state.faqContinuation = value
    },
    setIsRestoring(state, isRestoring) {
      state.isRestoring = isRestoring
    },
    setIsAuthorized(state, isAuthorized) {
      state.isAuthorized = isAuthorized
    },
    setToken(state, token) {
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
      state.token = token
    },
    setProfile(state, profile) {
      const nextProfile = profile?.subscriptions?.length
        ? { ...profile, subscriptions: sortSubscriptions(profile.subscriptions) }
        : profile

      state.profile = {
        ...state.profile,
        ...nextProfile,
      }
    },
    /**
     * Патч одной подписки по id: заменить или добавить, затем сортировка.
     * Полный список по-прежнему пишется через setProfile.
     */
    upsertSubscription(state, subscription) {
      if (subscription?.id == null || !state.profile) {
        return
      }

      const subscriptions = [...(state.profile.subscriptions ?? [])]
      const index = subscriptions.findIndex(sub => String(sub.id) === String(subscription.id))

      if (index === -1) {
        subscriptions.push(subscription)
      } else {
        subscriptions[index] = subscription
      }

      state.profile = {
        ...state.profile,
        subscriptions: sortSubscriptions(subscriptions),
      }
    },
    setDirectories(state, directories) {
      state.directories = directories
    },
    setBaseDirectories(state, directories) {
      state.baseDirectories = directories
    },
    setCurrentTrainingProgram(state, program) {
      state.currentTrainingProgram = program
    },
    /**
     * WS payment_update: кладём в paymentUpdate и параллельно
     * в profile.subscriptions по subscription_id:
     * — перезаписываем платёж по payment.id
     * — обновляем tg_chats на уровне подписки (как в HTTP userSubscriptions)
     */
    applyPaymentUpdate(state, payload) {
      if (payload == null) {
        state.paymentUpdate = null
        return
      }

      const payment = payload.payment ?? null
      const tg_chats = Array.isArray(payload.tg_chats) ? payload.tg_chats : []

      state.paymentUpdate = { payment, tg_chats }

      if (
        payment?.id == null
        || payment.subscription_id == null
        || !state.profile?.subscriptions?.length
      ) {
        return
      }

      const subscriptionId = String(payment.subscription_id)
      const paymentId = String(payment.id)

      let subscriptionFound = false

      const subscriptions = state.profile.subscriptions.map(sub => {
        if (String(sub.id) !== subscriptionId) {
          return sub
        }

        subscriptionFound = true
        const payments = [...(sub.payments ?? [])]
        const index = payments.findIndex(p => String(p.id) === paymentId)

        if (index === -1) {
          payments.push(payment)
        } else {
          payments[index] = payment
        }

        return { ...sub, payments, tg_chats }
      })

      if (!subscriptionFound) {
        return
      }

      state.profile = {
        ...state.profile,
        subscriptions: sortSubscriptions(subscriptions),
      }
    },
  },
  actions: {
    async updateProfile(context, payload) {
      const data = {
        ...payload,
      }

      const res = await appAxios.user.updateMyProfile(data)
      res.data = {
        ...res.data,
        ...payload,
      }
      context.commit('setProfile', res.data)
      return res
    },
    async fetchProfile(context) {
      const res = await appAxios.user.fetchMyProfile()

      if (!res.data?.length) {
        return null
      }

      context.commit('setProfile', res.data[0])
      return res.data[0]
    },
    async restore(context) {
      context.commit('setIsRestoring', true)
      await store.dispatch('app/init')

      const token = localStorage.getItem('token')
      if (token) {
        await context.dispatch('auth', {token})
      } else {
        if (appType === appTypes.TELEGRAM) {
          // if (appType === appTypes.WEB) {
          const initDataRaw = store.state.app.telegram.launchParams?.initDataRaw
          if (initDataRaw) {
            try {
              const res = await appAxios.telegram.send(initDataRaw)
              const {status, token} = res.data
              if (token && status === "success") {
                await context.dispatch('auth', {token})
              }
            } catch (err) {
              console.log('err response', err.response?.data)
              const {status, message} = err.response?.data ?? {}
              if (status === "error" && message === "User not found") {
                try {
                  const telegramSDK = await loadTelegramSdk()
                  const contactData = await telegramSDK.requestContact()
                  if (contactData.auth_date) {
                    contactData.authDate = contactData.auth_date
                    delete contactData.auth_date
                    contactData.contact = {
                      "userId": contactData.contact.user_id,
                      "phoneNumber": contactData.contact.phone_number,
                      "firstName": contactData.contact.first_name,
                      "lastName": contactData.contact.last_name,
                    }
                  }
                  //telegramSDK.isRequestingContact()
                  // const contactData = {
                  //   "contact": {
                  //     "userId": 639275338,
                  //     "phoneNumber": "+79960722023",
                  //     "firstName": "Timofey",
                  //     "lastName": "Timofeev"
                  //   },
                  //   "authDate": "2024-11-27T15:34:41.000Z",
                  //   "hash": "f11a2b7d918bfa283545acccc99b7237728c8185125be38efd6b6f23b101c527"
                  // }

                  const res = await appAxios.telegram.send(initDataRaw, contactData)
                  const {status, token} = res.data
                  if (token && status === "success") {
                    await context.dispatch('auth', {token})
                  }
                } catch (e) {
                  console.log('phone err', e)
                  // not provided
                }
              }
            }
          } else {
            console.warn('Telegram restore skipped: no initDataRaw')
          }
          // status          //   "success" // "error"
          // token          //   "00ec58f3b4307da441f2f689da2711437d9ef701"
        }
      }

      context.commit('setIsRestoring', false)

      if (!context.state.isAuthorized && router.currentRoute.value.query.returnTo) {
        const url = new URL(`${window.location.protocol}//${window.location.hostname}${decodeURIComponent(router.currentRoute.value.query.returnTo)}`)
        await router.push({
          path: url.pathname,
          query: Object.fromEntries(url.searchParams.entries()),
        })
      }
    },
    async login(context, payload) {
      const {phone, code, url} = payload
      try {
        const res = await appAxios.auth.checkAuthCode(phone, code)
        const token = res.data.token
        await context.dispatch('auth', {token, url})

        yandexMetrika('reachGoal', 'userAuthentication', {
          id: context.state.profile?.id,
        })
      } catch (e) {
        return {error: e}
      }
    },
    async auth(context, payload) {
      const {token, url} = payload
      context.commit('setToken', token)

      const res = await appAxios.user.fetchMyProfile()
      if (!res.data.length) {
        return await context.dispatch('logout')
      }

      context.commit('setProfile', res.data[0])

      await indexedStore.init(res.data[0].id)
      wsBroadcast.start()

      const res2 = await appAxios.user.fetchDirectories()
      const res3 = await appAxios.guest.fetchDirectories()

      context.commit('setDirectories', res2.data)
      context.commit('setBaseDirectories', res3.data)

      OneSignalDeferred.push(function () {
        OneSignal.login(res.data[0].id.toString())
      })

      yandexMetrika('setUserID', res.data[0].id.toString())
      yandexMetrika('reachGoal', 'userAuthorization', {
        id: context.state.profile?.id,
      })

      context.commit('setIsAuthorized', true)

      const returnTo = router.currentRoute.value.query.returnTo
      if (returnTo) {
        const url = new URL(decodeURIComponent(returnTo), window.location.origin)
        await router.push({
          path: url.pathname,
          query: Object.fromEntries(url.searchParams.entries()),
        })
        return
      }

      if (url) {
        await router.replace({name: url, params: {}})
        return
      }

      await router.push({name: 'Home'})
    },
    async logout(context) {
      await indexedStore.clear()
      localStorage.clear()

      context.commit('setIsAuthorized', null)
      context.commit('setToken', null)
      await router.push({name: 'LogIn'})
      context.commit('setProfile', null)
      context.commit('applyPaymentUpdate', null)
      wsBroadcast.stop()
      wsProgramBroadcast.stopProgramBroadcast()

      OneSignalDeferred.push(function () {
        OneSignal.logout()
      })
    },
    async desktopLogout(context, payload = {}) {
      await indexedStore.clear()
      localStorage.clear()

      context.commit('setIsAuthorized', null)
      context.commit('setToken', null)
      context.commit('setProfile', null)
      context.commit('applyPaymentUpdate', null)
      wsBroadcast.stop()
      wsProgramBroadcast.stopProgramBroadcast()

      OneSignalDeferred.push(function () {
        OneSignal.logout()
      })

      if(!payload.stay) {
        window.location.href = settings.env.VUE_APP_LANDING_URL
      } else {
        await router.push({name: 'DesktopAuth'})
      }
    },
    // TRAININGS
    async updateTargets(context, payload) {
      const data = {
        ...payload,
      }

      return await appAxios.targets.update(data)
    },
    async updateRecord(context, payload) {
      const data = {
        ...payload,
      }

      return await appAxios.records.update(data)
    },
    async updateTrainingFrequency(context, payload) {
      const data = {
        ...payload,
      }

      return await appAxios.training_frequency.update(data)
    },

    async createAIProgress(context, file) {
      return await appAxios.ai_progress.create(file)
    },
    async updateAIProgress(context, payload) {
      return await appAxios.ai_progress.update(payload)
    },

    async updateBodyProgress(context, payload) {
      const data = {
        ...payload,
      }

      return await appAxios.body_progress.update(data)
    },
    async updateHealthService(context, payload) {
      const data = {
        ...payload,
      }

      return await appAxios.health_service.update(data)
    },
    async updateHealthServiceActivity(context, payload) {
      const data = {
        ...payload,
      }

      return await appAxios.health_service_activity.update(data)
    },
    async createTrainingProgram(context, payload) {
      const data = {
        ...payload,
      }
      return await appAxios.my_training_programs.create(data)
    },
    async removeTrainingProgram(context, payload) {
      return await appAxios.my_training_programs.remove(payload)
    },
    async startTraining(context, id) {
      return await appAxios.my_training_programs.startTraining(id)
    },
    async restartTraining(context, id) {
      return await appAxios.my_training_programs.restartTraining(id)
    },
    async updateExercise(context, data) {
      return await appAxios.my_training_programs.updateExercise(data)
    },
    async setExerciseSetFinish(context, id) {
      return await appAxios.my_training_programs.setExerciseSetFinish(id)
    },
    async setTestExerciseFinish(context, id) {
      return await appAxios.my_training_programs.setTestExerciseFinish(id)
    }
  }
}
